import { DataPlaneClient } from "decodable-sdk-js/v1alpha2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useActiveAccount } from "../accounts/hooks/useAccounts";

const noHostErr = "Data plane hostname not configured";

export function useDataPlaneHost() {
  const { accountInfoIsLoading, accountInfoError, accountInfo } =
    useActiveAccount();

  const error = useMemo(
    () =>
      accountInfo && !accountInfo.data_plane_hostname
        ? new Error(noHostErr)
        : null,
    [accountInfo]
  );

  return {
    isLoading: accountInfoIsLoading,
    host: accountInfo?.data_plane_hostname,
    error: error ?? accountInfoError,
  };
}

// Returns a promise of a data plane client.
// If the data plane hostname is not configured, or the account info
// (which contains the hostname) cannot be fetched, the resolved
// client will throw errors for all fetch calls. This allows us to
// fail at the time the client is used, and avoid spamming the UI
// with errors at initialization time. It is normal for a data plane
// API hostname not to be configured during BYOC account setup,
// but it is an error to attempt to use data preview and other
// related features during this time. This is the reason the errors
// are deferred, rather than surfaced at initialization time.
export function useDataPlaneClient(): Promise<DataPlaneClient> {
  const { host, isLoading, error } = useDataPlaneHost();

  const ref = useRef<{
    host: string;
    explodedPromise: ExplodedPromise<DataPlaneClient>;
  }>();

  // Initialize the promise on mount, or re-initialize when the host changes.
  if (
    !ref.current ||
    (ref.current.explodedPromise.isFinalized &&
      (host ?? "") !== (ref.current.host ?? ""))
  ) {
    ref.current = {
      host: null,
      explodedPromise: explodedPromise<DataPlaneClient>(),
    };
  }

  if (!ref.current.explodedPromise.isFinalized) {
    if (host) {
      ref.current.host = host;
      ref.current.explodedPromise.resolve(new DataPlaneClient({ host }));
    } else if (error) {
      ref.current.explodedPromise.resolve(getUnusableDataPlaneClient(error));
    } else if (!isLoading && !host) {
      ref.current.explodedPromise.resolve(
        getUnusableDataPlaneClient(new Error(noHostErr))
      );
    }
  }

  return ref.current.explodedPromise.promise;
}

// An unusable data plane client will throw for all method invocations.
// See comment on useDataPlaneClient for why we do this, rather than
// failing at initialization time.
function getUnusableDataPlaneClient(error: Error) {
  return new DataPlaneClient({
    host: "<undefined>",
    fetch(_: any): Promise<any> {
      throw error;
    },
  });
}

type ExplodedPromise<T> = {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (err: Error) => void;
  isFinalized: boolean;
};

function explodedPromise<T>(): ExplodedPromise<T> {
  const exploded: ExplodedPromise<T> = {
    promise: null,
    resolve: null,
    reject: null,
    isFinalized: false,
  };

  exploded.promise = new Promise<T>((res, rej) => {
    exploded.resolve = res;
    exploded.reject = rej;
  });

  exploded.promise.finally(() => {
    exploded.isFinalized = true;
  });

  return exploded;
}
